import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

// markets_strip_v1() RPC is not in the generated Database type yet. TODO: regen types
// after migration lands; until then we call it via `as any` and narrow onto this local
// shape.
export interface MarketRow {
  ticker: string;
  symbol: string; // alias of ticker for legacy consumers
  name: string;
  price: number;
  change: number; // 1d absolute
  changePercent: number; // 1d percent (day_change_pct)
  change7d: number | null; // week_change_pct; null until IG price_history accrues
  change5d: number | null; // back-compat, never populated for IG
  change30d: number | null; // back-compat, never populated for IG
  marketState: string | null;
  fetchedAt: string | null;
}

// Maps a markets_strip_v1() row: ticker, label, price, market_state, epic,
// day_change_pct, week_change_pct, as_of.
function mapRow(r: any): MarketRow {
  return {
    ticker: r.ticker,
    symbol: r.ticker,
    name: r.label ?? r.ticker,
    price: Number(r.price ?? 0),
    change: Number(r.day_change_pct ?? 0),
    changePercent: Number(r.day_change_pct ?? 0),
    change7d: r.week_change_pct == null ? null : Number(r.week_change_pct),
    change5d: null,
    change30d: null,
    marketState: r.market_state ?? null,
    fetchedAt: r.as_of ?? null,
  };
}

/**
 * Live IG market snapshot.
 *
 * - Initial read: supabase.rpc("markets_strip_v1") — the shared day/7d contract also
 *   consumed by mobile Markets and Android Auto.
 * - Live updates: a postgres_changes subscription on market_data_cache triggers a
 *   refetch of the RPC (the raw table row doesn't carry day/week % so it can't be
 *   ingested directly), replacing the old 30s polling loop.
 * - Fallback: if the channel is not SUBSCRIBED within ~5s, poll the ig-market-data
 *   edge function (no body == get_latest) every ~20s until the channel comes up.
 */
export function useMarketData() {
  const [rows, setRows] = useState<MarketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const rowsByTicker = useRef<Map<string, MarketRow>>(new Map());

  const commit = useCallback(() => {
    const next = Array.from(rowsByTicker.current.values()).sort((a, b) =>
      a.ticker.localeCompare(b.ticker),
    );
    setRows(next);
  }, []);

  const ingest = useCallback(
    (list: MarketRow[]) => {
      for (const row of list) rowsByTicker.current.set(row.ticker, row);
      commit();
    },
    [commit],
  );

  const fetchInitial = useCallback(async () => {
    // TODO: regen types — RPC isn't typed yet, so call via `as any` and narrow via mapRow.
    const { data, error } = await (supabase as any).rpc("markets_strip_v1");
    if (!error && Array.isArray(data)) ingest((data as any[]).map(mapRow));
    setLoading(false);
  }, [ingest]);

  // Fallback poll of the edge function (no body -> get_latest legacy shape).
  const pollEdge = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("ig-market-data");
      if (error) throw error;
      if (data?.success && Array.isArray(data.data)) {
        ingest(
          (data.data as any[]).map((d) => ({
            ticker: d.ticker ?? d.symbol,
            symbol: d.symbol ?? d.ticker,
            name: d.name,
            price: Number(d.price ?? 0),
            change: Number(d.change ?? 0),
            changePercent: Number(d.changePercent ?? 0),
            change7d: null,
            change5d: null,
            change30d: null,
            marketState: d.marketState ?? null,
            fetchedAt: d.fetchedAt ?? null,
          })),
        );
      }
    } catch (e) {
      console.error("ig-market-data poll fallback failed:", e);
    }
  }, [ingest]);

  useEffect(() => {
    let disposed = false;
    let chan: ReturnType<typeof supabase.channel> | null = null;
    let watchdog: ReturnType<typeof setTimeout> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };
    const startPolling = () => {
      if (pollTimer) return;
      void pollEdge(); // immediate, then every ~20s
      pollTimer = setInterval(() => {
        void pollEdge();
      }, 20000);
    };

    void fetchInitial();

    chan = supabase
      .channel("market-data-ig")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "market_data_cache", filter: "source=eq.ig" },
        () => {
          // The raw table row doesn't carry day_change_pct/week_change_pct, so a change
          // notification just triggers a refetch of the markets_strip_v1() contract.
          void fetchInitial();
        },
      )
      .subscribe((status: string) => {
        if (disposed) return;
        if (status === "SUBSCRIBED") {
          if (watchdog) {
            clearTimeout(watchdog);
            watchdog = null;
          }
          stopPolling();
        }
      });

    // If the channel hasn't come up within ~5s, fall back to polling.
    watchdog = setTimeout(() => {
      if (!disposed) startPolling();
    }, 5000);

    return () => {
      disposed = true;
      if (watchdog) clearTimeout(watchdog);
      stopPolling();
      if (chan) supabase.removeChannel(chan);
    };
  }, [fetchInitial, pollEdge, commit]);

  return { rows, loading };
}
